import { LANGUAGES } from "@bt/parts-core";
import { z } from "zod";
import { email } from "./accounts";

/**
 * The public demo (ADR 0012): a visitor asks for a link by email and opens a demo garage of their
 * own, filled from the demo package, which is deleted with everything in it after a while.
 */

/** How long a demo garage lives unless the server says otherwise (BT_DEMO_GARAGE_HOURS). */
export const DEMO_GARAGE_HOURS = 48;
/** How long an emailed link works. */
export const DEMO_LINK_MINUTES = 60;
/** How many demo garages may live at once; above it, the demo is busy. */
export const DEMO_GARAGE_MAX = 100;
/** Every demo garage's name starts with this reserved word, so no other garage looks like one. */
export const DEMO_GARAGE_PREFIX = "demo-";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/** When a demo garage opened at `now` is deleted. */
export function demoGarageExpiry(now: Date, hours: number): Date {
  return new Date(now.getTime() + hours * HOUR);
}

/** When a link sent at `now` stops working. */
export function demoLinkExpiry(now: Date): Date {
  return new Date(now.getTime() + DEMO_LINK_MINUTES * MINUTE);
}

/** Whole hours until a demo garage is deleted, rounded up ("deleted in 47 h"); 0 once it is due. */
export function demoHoursLeft(expiresAt: string, now: Date): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / HOUR));
}

/**
 * Whether the sweeper deletes a demo now: an opened garage once it expires, a request nobody
 * opened once its link expires. An opened garage's newer link doesn't matter.
 */
export function demoGarageDue(
  demo: { opened: boolean; expiresAt: string | null; linkExpiresAt: string | null },
  now: Date,
): boolean {
  const until = demo.opened ? demo.expiresAt : demo.linkExpiresAt;
  return until === null || new Date(until).getTime() <= now.getTime();
}

/**
 * Asking for a link. `website` is a field people never see (a honeypot): a request that fills it
 * gets the usual answer and nothing else.
 */
export const demoRequestInput = z.object({
  email,
  language: z.enum(LANGUAGES),
  website: z.string().max(500).optional(),
});
export type DemoRequestInput = z.infer<typeof demoRequestInput>;

/** The welcome steps' last one: the visitor's demo garage, with the example data or empty. */
export const demoStartInput = z.object({
  exampleData: z.boolean(),
  language: z.enum(LANGUAGES),
});
export type DemoStartInput = z.infer<typeof demoStartInput>;
